import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Check, X, Loader2, Users, FileText, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/api';
import { fmtRange, fmtDayCount, fmtHourCount } from '../../utils/format';

// ─── "Needs your approval" on the dashboard (admins and managers) ──────────
// From the server summary: only the people you approve, soonest first, each
// with who else in their department is off at the same time. Approve or
// reject without leaving the dashboard.
export function ApprovalPanel({ limit = 6 }) {
  const { summary, refreshSummary, updateStatus, isAdmin } = useApp();
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  // Approving would take them beyond their balance: { id, message }
  const [overWarn, setOverWarn] = useState(null);
  const list = summary?.approvals ?? [];
  if (!summary?.isApprover) return null;

  const today = startOfDay(new Date());

  async function decide(r, action, { anyway = false } = {}) {
    setBusy(`${r.id}:${action}`);
    try {
      if (action === 'approve') await apiService.approveLeaveRequest(r.id, '', anyway ? { allowOverdraw: true } : {});
      else await apiService.rejectLeaveRequest(r.id, note.trim());
      updateStatus?.(r.id, action === 'approve' ? 'approved' : 'rejected');
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${r.name.split(' ')[0]}'s request${anyway ? ' (beyond their balance)' : ''}`);
      setRejecting(null);
      setOverWarn(null);
      setNote('');
      await refreshSummary?.();
    } catch (e) {
      const data = e?.response?.data;
      if (action === 'approve' && e?.response?.status === 409 && data?.code === 'OVER_BALANCE') {
        setRejecting(null);
        setOverWarn({ id: r.id, message: data.check?.message || data.message });
      } else {
        toast.error(data?.message || 'That didn’t work. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="db-card" style={{ overflow: 'hidden' }} aria-labelledby="ap-title">
      <style>{`
        .ap-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px 14px; align-items: center; padding: 12px 20px; }
        .ap-row + .ap-row { border-top: 1px solid var(--border-soft); }
        .ap-clash { grid-column: 1 / -1; display: flex; align-items: flex-start; gap: 6px; font-size: 12px; color: var(--warn-text); background: var(--warn-bg-soft); border: 1px solid var(--warn-border); border-radius: 10px; padding: 6px 10px; }
        .ap-btn { display: inline-flex; align-items: center; gap: 5px; height: 32px; padding: 0 12px; border-radius: 10px; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
        .ap-ok { background: #059669; color: #fff; border: 1px solid #059669; }
        .ap-no { background: var(--surface); color: var(--bad-text); border: 1px solid var(--field-border); }
        @media (max-width: 560px) { .ap-row { grid-template-columns: 1fr; } }
      `}</style>
      <div className="db-card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="db-card-icon" style={{ background: 'var(--warn-bg)', color: 'var(--warn-text)' }}><Inbox size={15} /></span>
          <h2 id="ap-title" className="db-card-title">Needs your approval</h2>
          <span className={'db-pill' + (list.length ? ' db-pill-warn' : '')}>{summary.pendingForMe}</span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {summary.scope === 'team' ? 'Your team · soonest first' : 'Soonest first'}
        </span>
      </div>

      {list.length === 0 ? (
        <p style={{ margin: 0, padding: '18px 20px', fontSize: 13, color: 'var(--text-muted)' }}>Nothing waiting for you. New requests from {isAdmin ? 'the company' : 'your team'} will appear here.</p>
      ) : list.slice(0, limit).map(r => {
        const startsIn = differenceInCalendarDays(parseISO(r.from), today);
        const amount = r.period === 'custom' ? fmtHourCount(r.hours) : fmtDayCount(r.days);
        const clashes = (r.clashes ?? []).filter(c => c.status === 'approved' || c.status === 'pending');
        return (
          <div key={r.id} className="ap-row">
            <div style={{ minWidth: 0 }}>
              <p className="db-row-title">{r.name} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {r.type}</span></p>
              <p className="db-row-sub">
                {fmtRange(r.from, r.to)} · {amount}
                {' · '}
                <span style={{ color: startsIn <= 7 ? 'var(--warn-text)' : undefined, fontWeight: startsIn <= 7 ? 600 : 400 }}>
                  {startsIn < 0 ? 'already started' : startsIn === 0 ? 'starts today' : startsIn === 1 ? 'starts tomorrow' : `starts in ${startsIn} days`}
                </span>
                {(r.overdrawn || r.balanceCheck?.overdraw) && <span style={{ color: 'var(--bad-text)', fontWeight: 600 }} title={r.balanceCheck?.message || undefined}> · beyond balance</span>}
                {r.documentRequired && <span> · <FileText size={11} style={{ display: 'inline', verticalAlign: '-1px' }} /> {r.hasDocument ? 'sick note with HR' : 'sick note needed'}</span>}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button type="button" className="ap-btn ap-ok" disabled={!!busy || overWarn?.id === r.id} onClick={() => decide(r, 'approve')}>
                {busy === `${r.id}:approve` ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve
              </button>
              <button type="button" className="ap-btn ap-no" disabled={!!busy} aria-expanded={rejecting === r.id} onClick={() => { setRejecting(rejecting === r.id ? null : r.id); setNote(''); }}>
                <X size={13} /> Reject
              </button>
            </div>
            {clashes.length > 0 && (
              <div className="ap-clash">
                {clashes.length >= 2 ? <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} /> : <Users size={13} style={{ marginTop: 1, flexShrink: 0 }} />}
                <span>
                  Also off: {clashes.slice(0, 4).map(c => `${c.name} (${c.label}${c.status === 'pending' ? ', pending' : ''}, ${fmtRange(c.from, c.to, { weekday: false })})`).join('; ')}
                  {clashes.length > 4 ? ` and ${clashes.length - 4} more` : ''}
                </span>
              </div>
            )}
            {overWarn?.id === r.id && (
              <div role="alert" style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--warn-text)', background: 'var(--warn-bg-soft)', border: '1px solid var(--warn-border)', borderRadius: 10, padding: '8px 10px' }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 200 }}><b>Beyond their balance.</b> {overWarn.message}</span>
                <button type="button" className="ap-btn" style={{ background: 'var(--surface)', border: '1px solid var(--field-border)', color: 'var(--text-2)' }} disabled={!!busy} onClick={() => setOverWarn(null)}>Don’t approve</button>
                <button type="button" className="ap-btn ap-ok" disabled={!!busy} onClick={() => decide(r, 'approve', { anyway: true })}>
                  {busy === `${r.id}:approve` ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve anyway
                </button>
              </div>
            )}
            {rejecting === r.id && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  className="form-input"
                  style={{ flex: 1, minWidth: 200 }}
                  autoFocus
                  value={note}
                  maxLength={500}
                  onChange={e => setNote(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') decide(r, 'reject'); if (e.key === 'Escape') setRejecting(null); }}
                  placeholder={`Reason (optional, ${r.name.split(' ')[0]} will see it)`}
                  aria-label="Reason for rejecting"
                />
                <button type="button" className="ap-btn" style={{ background: '#DC2626', color: '#fff', border: '1px solid #DC2626' }} disabled={!!busy} onClick={() => decide(r, 'reject')}>
                  {busy === `${r.id}:reject` ? <Loader2 size={13} className="animate-spin" /> : null} Reject request
                </button>
              </div>
            )}
          </div>
        );
      })}
      {list.length > limit && (
        <p style={{ margin: 0, padding: '10px 20px', borderTop: '1px solid var(--border-soft)', fontSize: 12.5 }}>
          <Link to="/requests" style={{ color: 'var(--accent)', fontWeight: 600 }}>See all {summary.pendingForMe} waiting</Link>
        </p>
      )}
    </section>
  );
}

export default ApprovalPanel;